import {createContext, useState} from "react";

import {backend, BackendErrorType, BackendResult} from "../model/Backend";
import {unimplementedFunction} from "../common/Utils";
import {LoadingTracker, ObjectWithTracker} from "./ContextUtils";

const UserContext = createContext(null);
const UserDispatchContext = createContext(unimplementedFunction);

class UserActions {
    static RequestLogin = "request_login";
    // TODO: this is only used in RegisterModal and should probably be eliminated
    static Login = "login";
    static TryRestoreLogin = "try_restore_login";
    static Logout = "logout";
    static AddActivity = "add_activity";
    static RemoveActivity = "remove_activity";
    static DeleteErrors = "delete_errors";
}

function useUserReducer() {
    const [user, setUser] = useState(new ObjectWithTracker(null));

    function userDispatch(actionData) {
        const action = _createAction(actionData);
        action.execute(setUser);
    }

    return [user, userDispatch];
}

class SimpleAction {
    constructor(newUserCreator) {
        this.newUserCreator = newUserCreator;
    }

    execute(setUser) {
        setUser(this.newUserCreator);
    }
}

class ActionWithSideEffects {
    constructor(backendPromise, trackingId, onSuccess) {
        this.backendPromise = backendPromise;
        this.trackingId = trackingId;
        this.onSuccess = onSuccess;
    }

    execute(setUser) {
        const trackingId = this.trackingId;
        const onSuccess = this.onSuccess;

        function modifyUserWithResult(oldUser, actionResult) {
            const newUser = oldUser.clone();
            newUser.status.stopLoading(trackingId);
            if (actionResult.isSuccess()) {
                onSuccess(newUser, actionResult);
            } else {
                console.log(actionResult);
                newUser.status.setError(actionResult, trackingId);
            }
            return newUser;
        }

        function onResponse(result) {
            setUser(u => modifyUserWithResult(u, result))
        }

        this.backendPromise
            .then(onResponse)
            .catch(error => {
                console.error(error);
                onResponse(BackendResult.FromError(BackendErrorType.GeneralError));
            });

        setUser(u => {
            const clonedUser = u.clone();
            clonedUser.status.deleteAllErrors();
            clonedUser.status.startLoading(this.trackingId);
            return clonedUser;
        });
    }
}

function _createAction(actionData) {
    if (actionData.type === UserActions.RequestLogin) {
        return new ActionWithSideEffects(
            backend.login(actionData.loginData),
            LoadingTracker.defaultId,
            (user, result) => user.value = result.data,
        );
    } else if (actionData.type === UserActions.Login) {
        return new SimpleAction(oldUser => {
            if (oldUser.value !== null) {
                console.warn("Logged in event though was already logged in.");
            }
            return new ObjectWithTracker(actionData.user);
        });
    } else if (actionData.type === UserActions.TryRestoreLogin) {
        return new ActionWithSideEffects(
            backend.checkIfLoggedIn(),
            LoadingTracker.defaultId,
            (user, result) => user.value = result.data,
        );
    } else if (actionData.type === UserActions.Logout) {
        backend.logout();
        return new SimpleAction(oldUser => {
            if (oldUser.value === null) {
                console.warn("Logged out event though wasn't logged in.");
            }
            return new ObjectWithTracker(null);
        });
    } else if (actionData.type === UserActions.DeleteErrors) {
        return new SimpleAction(oldUser => {
            const user = oldUser.clone();
            if (user.value === null)
                return user;
            user.status.deleteAllErrors();
            return user;
        });
    } else if (actionData.type === UserActions.AddActivity) {
        return new ActionWithSideEffects(
            backend.addUserActivity(actionData.activity),
            actionData.activity.route.id,
            (user, result) => user.value.activities.push(actionData.activity),
        );
    } else if (actionData.type === UserActions.RemoveActivity) {
        const routeId = actionData.activity.routeId;
        return new ActionWithSideEffects(
            backend.removeUserActivity(actionData.activity),
            routeId,
            (user, result) => user.value.activities = user.value.activities.filter(a =>
                !(actionData.activity.type === a.type && routeId === a.route.id)
            ),
        );
    } else {
        throw Error("Unknown action: " + actionData.type);
    }

}

export {UserContext, UserDispatchContext, UserActions, useUserReducer};